import crossFetch from 'cross-fetch';
import { pluginId } from '../pluginId.esm.js';
import * as parser from 'uri-template';

class DefaultApiClient {
  discoveryApi;
  fetchApi;
  constructor(options) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi || { fetch: crossFetch };
  }
  /**
   * Delete a single entity by UID.
   * @param uid -
   */
  async deleteEntityByUid(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/entities/by-uid/{uid}`;
    const uri = parser.parse(uriTemplate).expand({
      uid: request.path.uid
    });
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "DELETE"
    });
  }
  /**
       * Get all entities matching a given filter.
       * @param fields - By default the full entities are returned, but you can pass in a &#x60;fields&#x60; query
  parameter which selects what parts of the entity data to retain. This makes the
  response smaller and faster to transfer, and may allow the catalog to perform
  more efficient queries.
  
  The query parameter value is a comma separated list of simplified JSON paths
  like above. Each path corresponds to the key of either a value, or of a subtree
  root that you want to keep in the output. The rest is pruned away. For example,
  specifying &#x60;?fields&#x3D;metadata.name,metadata.annotations,spec&#x60; retains only the
  &#x60;name&#x60; and &#x60;annotations&#x60; fields of the &#x60;metadata&#x60; of each entity (it&#39;ll be an
  object with at most two keys), keeps the entire &#x60;spec&#x60; unchanged, and cuts out
  all other roots such as &#x60;relations&#x60;.
  
  Some more real world usable examples:
  
  - Return only enough data to form the full ref of each entity:
  
    &#x60;/entities/by-query?fields&#x3D;kind,metadata.namespace,metadata.name&#x60;
  
       * @param limit - Number of records to return in the response.
       * @param filter - You can pass in one or more filter sets that get matched against each entity.
  Each filter set is a number of conditions that all have to match for the
  condition to be true (conditions effectively have an AND between them). At least
  one filter set has to be true for the entity to be part of the result set
  (filter sets effectively have an OR between them).
  
  Example:
  
  &#x60;&#x60;&#x60;text
  /entities/by-query?filter&#x3D;kind&#x3D;user,metadata.namespace&#x3D;default&amp;filter&#x3D;kind&#x3D;group,spec.type
  
    Return entities that match
  
      Filter set 1:
        Condition 1: kind &#x3D; user
                    AND
        Condition 2: metadata.namespace &#x3D; default
  
      OR
  
      Filter set 2:
        Condition 1: kind &#x3D; group
                    AND
        Condition 2: spec.type exists
  &#x60;&#x60;&#x60;
  
  Each condition is either on the form &#x60;&lt;key&gt;&#x60;, or on the form &#x60;&lt;key&gt;&#x3D;&lt;value&gt;&#x60;.
  The first form asserts on the existence of a certain key (with any value), and
  the second asserts that the key exists and has a certain value. All checks are
  always case _insensitive_.
  
  In all cases, the key is a simplified JSON path in a given piece of entity data.
  Each part of the path is a key of an object, and the traversal also descends
  through arrays. There are two special forms:
  
  - Array items that are simple value types (such as strings) match on a key-value
    pair where the key is the item as a string, and the value is the string &#x60;true&#x60;
  - Relations can be matched on a &#x60;relations.&lt;type&gt;&#x3D;&lt;targetRef&gt;&#x60; form
  
  Let&#39;s look at a simplified example to illustrate the concept:
  
  &#x60;&#x60;&#x60;json
  {
    &quot;a&quot;: {
      &quot;b&quot;: [&quot;c&quot;, { &quot;d&quot;: 1 }],
      &quot;e&quot;: 7
    }
  }
  &#x60;&#x60;&#x60;
  
  This would match any one of the following conditions:
  
  - &#x60;a&#x60;
  - &#x60;a.b&#x60;
  - &#x60;a.b.c&#x60;
  - &#x60;a.b.c&#x3D;true&#x60;
  - &#x60;a.b.d&#x60;
  - &#x60;a.b.d&#x3D;1&#x60;
  - &#x60;a.e&#x60;
  - &#x60;a.e&#x3D;7&#x60;
  
  Some more real world usable examples:
  
  - Return all orphaned entities:
  
    &#x60;/entities/by-query?filter&#x3D;metadata.annotations.backstage.io/orphan&#x3D;true&#x60;
  
  - Return all users and groups:
  
    &#x60;/entities/by-query?filter&#x3D;kind&#x3D;user&amp;filter&#x3D;kind&#x3D;group&#x60;
  
  - Return all service components:
  
    &#x60;/entities/by-query?filter&#x3D;kind&#x3D;component,spec.type&#x3D;service&#x60;
  
  - Return all entities with the &#x60;java&#x60; tag:
  
    &#x60;/entities/by-query?filter&#x3D;metadata.tags.java&#x60;
  
  - Return all users who are members of the &#x60;ops&#x60; group (note that the full
    [reference](references.md) of the group is used):
  
    &#x60;/entities/by-query?filter&#x3D;kind&#x3D;user,relations.memberof&#x3D;group:default/ops&#x60;
  
       * @param offset - Number of records to skip in the query page.
       * @param after - Pointer to the previous page of results.
       * @param order - 
       */
  async getEntities(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/entities{?fields,limit,filter*,offset,after,order*}`;
    const uri = parser.parse(uriTemplate).expand({
      ...request.query
    });
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "GET"
    });
  }
  /**
       * Search for entities by a given query.
       * @param fields - By default the full entities are returned, but you can pass in a &#x60;fields&#x60; query
  parameter which selects what parts of the entity data to retain. This makes the
  response smaller and faster to transfer, and may allow the catalog to perform
  more efficient queries.
  
  The query parameter value is a comma separated list of simplified JSON paths
  like above. Each path corresponds to the key of either a value, or of a subtree
  root that you want to keep in the output. The rest is pruned away. For example,
  specifying &#x60;?fields&#x3D;metadata.name,metadata.annotations,spec&#x60; retains only the
  &#x60;name&#x60; and &#x60;annotations&#x60; fields of the &#x60;metadata&#x60; of each entity (it&#39;ll be an
  object with at most two keys), keeps the entire &#x60;spec&#x60; unchanged, and cuts out
  all other roots such as &#x60;relations&#x60;.
  
  Some more real world usable examples:
  
  - Return only enough data to form the full ref of each entity:
  
    &#x60;/entities/by-query?fields&#x3D;kind,metadata.namespace,metadata.name&#x60;
  
       * @param limit - Number of records to return in the response.
       * @param offset - Number of records to skip in the query page.
       * @param orderField - By default the entities are returned ordered by their internal uid. You can
  customize the &#x60;orderField&#x60; query parameters to affect that ordering.
  
  For example, to return entities by their name:
  
  &#x60;/entities/by-query?orderField&#x3D;metadata.name,asc&#x60;
  
  Each parameter can be followed by &#x60;asc&#x60; for ascending lexicographical order or
  &#x60;desc&#x60; for descending (reverse) lexicographical order.
  
       * @param cursor - You may pass the &#x60;cursor&#x60; query parameters to perform cursor based pagination
  through the set of entities. The value of &#x60;cursor&#x60; will be returned in the response, under the &#x60;pageInfo&#x60; property:
  
  &#x60;&#x60;&#x60;json
    &quot;pageInfo&quot;: {
      &quot;nextCursor&quot;: &quot;a-cursor&quot;,
      &quot;prevCursor&quot;: &quot;another-cursor&quot;
    }
  &#x60;&#x60;&#x60;
  
  If &#x60;nextCursor&#x60; exists, it can be used to retrieve the next batch of entities. Following the same approach,
  if &#x60;prevCursor&#x60; exists, it can be used to retrieve the previous batch of entities.
  
  - [&#x60;filter&#x60;](#filtering), for selecting only a subset of all entities
  - [&#x60;fields&#x60;](#field-selection), for selecting only parts of the full data
    structure of each entity
  - &#x60;limit&#x60; for limiting the number of entities returned (20 is the default)
  - [&#x60;orderField&#x60;](#ordering), for deciding the order of the entities
  - &#x60;fullTextFilter&#x60;
    **NOTE**: [&#x60;filter&#x60;, &#x60;orderField&#x60;, &#x60;fullTextFilter&#x60;] and &#x60;cursor&#x60; are mutually exclusive. This means that,
    it isn&#39;t possible to change any of [&#x60;filter&#x60;, &#x60;orderField&#x60;, &#x60;fullTextFilter&#x60;] when passing &#x60;cursor&#x60; as query parameters,
    as changing any of these properties will affect pagination. If any of &#x60;filter&#x60;, &#x60;orderField&#x60;, &#x60;fullTextFilter&#x60; is specified together with &#x60;cursor&#x60;, only the latter is taken into consideration.
  
       * @param filter - You can pass in one or more filter sets that get matched against each entity.
  Each filter set is a number of conditions that all have to match for the
  condition to be true (conditions effectively have an AND between them). At least
  one filter set has to be true for the entity to be part of the result set
  (filter sets effectively have an OR between them).
  
  Example:
  
  &#x60;&#x60;&#x60;text
  /entities/by-query?filter&#x3D;kind&#x3D;user,metadata.namespace&#x3D;default&amp;filter&#x3D;kind&#x3D;group,spec.type
  
    Return entities that match
  
      Filter set 1:
        Condition 1: kind &#x3D; user
                    AND
        Condition 2: metadata.namespace &#x3D; default
  
      OR
  
      Filter set 2:
        Condition 1: kind &#x3D; group
                    AND
        Condition 2: spec.type exists
  &#x60;&#x60;&#x60;
  
  Each condition is either on the form &#x60;&lt;key&gt;&#x60;, or on the form &#x60;&lt;key&gt;&#x3D;&lt;value&gt;&#x60;.
  The first form asserts on the existence of a certain key (with any value), and
  the second asserts that the key exists and has a certain value. All checks are
  always case _insensitive_.
  
  In all cases, the key is a simplified JSON path in a given piece of entity data.
  Each part of the path is a key of an object, and the traversal also descends
  through arrays. There are two special forms:
  
  - Array items that are simple value types (such as strings) match on a key-value
    pair where the key is the item as a string, and the value is the string &#x60;true&#x60;
  - Relations can be matched on a &#x60;relations.&lt;type&gt;&#x3D;&lt;targetRef&gt;&#x60; form
  
  Let&#39;s look at a simplified example to illustrate the concept:
  
  &#x60;&#x60;&#x60;json
  {
    &quot;a&quot;: {
      &quot;b&quot;: [&quot;c&quot;, { &quot;d&quot;: 1 }],
      &quot;e&quot;: 7
    }
  }
  &#x60;&#x60;&#x60;
  
  This would match any one of the following conditions:
  
  - &#x60;a&#x60;
  - &#x60;a.b&#x60;
  - &#x60;a.b.c&#x60;
  - &#x60;a.b.c&#x3D;true&#x60;
  - &#x60;a.b.d&#x60;
  - &#x60;a.b.d&#x3D;1&#x60;
  - &#x60;a.e&#x60;
  - &#x60;a.e&#x3D;7&#x60;
  
  Some more real world usable examples:
  
  - Return all orphaned entities:
  
    &#x60;/entities/by-query?filter&#x3D;metadata.annotations.backstage.io/orphan&#x3D;true&#x60;
  
  - Return all users and groups:
  
    &#x60;/entities/by-query?filter&#x3D;kind&#x3D;user&amp;filter&#x3D;kind&#x3D;group&#x60;
  
  - Return all service components:
  
    &#x60;/entities/by-query?filter&#x3D;kind&#x3D;component,spec.type&#x3D;service&#x60;
  
  - Return all entities with the &#x60;java&#x60; tag:
  
    &#x60;/entities/by-query?filter&#x3D;metadata.tags.java&#x60;
  
  - Return all users who are members of the &#x60;ops&#x60; group (note that the full
    [reference](references.md) of the group is used):
  
    &#x60;/entities/by-query?filter&#x3D;kind&#x3D;user,relations.memberof&#x3D;group:default/ops&#x60;
  
       * @param fullTextFilterTerm - Text search term.
       * @param fullTextFilterFields - A comma separated list of fields to sort returned results by.
       */
  async getEntitiesByQuery(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/entities/by-query{?fields,limit,offset,orderField*,cursor,filter*,fullTextFilterTerm,fullTextFilterFields}`;
    const uri = parser.parse(uriTemplate).expand({
      ...request.query
    });
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "GET"
    });
  }
  /**
       * Get a batch set of entities given an array of entityRefs.
       * @param filter - You can pass in one or more filter sets that get matched against each entity.
  Each filter set is a number of conditions that all have to match for the
  condition to be true (conditions effectively have an AND between them). At least
  one filter set has to be true for the entity to be part of the result set
  (filter sets effectively have an OR between them).
  
  Example:
  
  &#x60;&#x60;&#x60;text
  /entities/by-query?filter&#x3D;kind&#x3D;user,metadata.namespace&#x3D;default&amp;filter&#x3D;kind&#x3D;group,spec.type
  
    Return entities that match
  
      Filter set 1:
        Condition 1: kind &#x3D; user
                    AND
        Condition 2: metadata.namespace &#x3D; default
  
      OR
  
      Filter set 2:
        Condition 1: kind &#x3D; group
                    AND
        Condition 2: spec.type exists
  &#x60;&#x60;&#x60;
  
  Each condition is either on the form &#x60;&lt;key&gt;&#x60;, or on the form &#x60;&lt;key&gt;&#x3D;&lt;value&gt;&#x60;.
  The first form asserts on the existence of a certain key (with any value), and
  the second asserts that the key exists and has a certain value. All checks are
  always case _insensitive_.
  
  In all cases, the key is a simplified JSON path in a given piece of entity data.
  Each part of the path is a key of an object, and the traversal also descends
  through arrays. There are two special forms:
  
  - Array items that are simple value types (such as strings) match on a key-value
    pair where the key is the item as a string, and the value is the string &#x60;true&#x60;
  - Relations can be matched on a &#x60;relations.&lt;type&gt;&#x3D;&lt;targetRef&gt;&#x60; form
  
  Let&#39;s look at a simplified example to illustrate the concept:
  
  &#x60;&#x60;&#x60;json
  {
    &quot;a&quot;: {
      &quot;b&quot;: [&quot;c&quot;, { &quot;d&quot;: 1 }],
      &quot;e&quot;: 7
    }
  }
  &#x60;&#x60;&#x60;
  
  This would match any one of the following conditions:
  
  - &#x60;a&#x60;
  - &#x60;a.b&#x60;
  - &#x60;a.b.c&#x60;
  - &#x60;a.b.c&#x3D;true&#x60;
  - &#x60;a.b.d&#x60;
  - &#x60;a.b.d&#x3D;1&#x60;
  - &#x60;a.e&#x60;
  - &#x60;a.e&#x3D;7&#x60;
  
  Some more real world usable examples:
  
  - Return all orphaned entities:
  
    &#x60;/entities/by-query?filter&#x3D;metadata.annotations.backstage.io/orphan&#x3D;true&#x60;
  
  - Return all users and groups:
  
    &#x60;/entities/by-query?filter&#x3D;kind&#x3D;user&amp;filter&#x3D;kind&#x3D;group&#x60;
  
  - Return all service components:
  
    &#x60;/entities/by-query?filter&#x3D;kind&#x3D;component,spec.type&#x3D;service&#x60;
  
  - Return all entities with the &#x60;java&#x60; tag:
  
    &#x60;/entities/by-query?filter&#x3D;metadata.tags.java&#x60;
  
  - Return all users who are members of the &#x60;ops&#x60; group (note that the full
    [reference](references.md) of the group is used):
  
    &#x60;/entities/by-query?filter&#x3D;kind&#x3D;user,relations.memberof&#x3D;group:default/ops&#x60;
  
       * @param getEntitiesByRefsRequest - 
       */
  async getEntitiesByRefs(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/entities/by-refs{?filter*}`;
    const uri = parser.parse(uriTemplate).expand({
      ...request.query
    });
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "POST",
      body: JSON.stringify(request.body)
    });
  }
  /**
   * Get an entity's ancestry by entity ref.
   * @param kind -
   * @param namespace -
   * @param name -
   */
  async getEntityAncestryByName(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/entities/by-name/{kind}/{namespace}/{name}/ancestry`;
    const uri = parser.parse(uriTemplate).expand({
      kind: request.path.kind,
      namespace: request.path.namespace,
      name: request.path.name
    });
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "GET"
    });
  }
  /**
   * Get an entity by an entity ref.
   * @param kind -
   * @param namespace -
   * @param name -
   */
  async getEntityByName(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/entities/by-name/{kind}/{namespace}/{name}`;
    const uri = parser.parse(uriTemplate).expand({
      kind: request.path.kind,
      namespace: request.path.namespace,
      name: request.path.name
    });
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "GET"
    });
  }
  /**
   * Get a single entity by the UID.
   * @param uid -
   */
  async getEntityByUid(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/entities/by-uid/{uid}`;
    const uri = parser.parse(uriTemplate).expand({
      uid: request.path.uid
    });
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "GET"
    });
  }
  /**
       * Get all entity facets that match the given filters.
       * @param facet - 
       * @param filter - You can pass in one or more filter sets that get matched against each entity.
  Each filter set is a number of conditions that all have to match for the
  condition to be true (conditions effectively have an AND between them). At least
  one filter set has to be true for the entity to be part of the result set
  (filter sets effectively have an OR between them).
  
  Example:
  
  &#x60;&#x60;&#x60;text
  /entities/by-query?filter&#x3D;kind&#x3D;user,metadata.namespace&#x3D;default&amp;filter&#x3D;kind&#x3D;group,spec.type
  
    Return entities that match
  
      Filter set 1:
        Condition 1: kind &#x3D; user
                    AND
        Condition 2: metadata.namespace &#x3D; default
  
      OR
  
      Filter set 2:
        Condition 1: kind &#x3D; group
                    AND
        Condition 2: spec.type exists
  &#x60;&#x60;&#x60;
  
  Each condition is either on the form &#x60;&lt;key&gt;&#x60;, or on the form &#x60;&lt;key&gt;&#x3D;&lt;value&gt;&#x60;.
  The first form asserts on the existence of a certain key (with any value), and
  the second asserts that the key exists and has a certain value. All checks are
  always case _insensitive_.
  
  In all cases, the key is a simplified JSON path in a given piece of entity data.
  Each part of the path is a key of an object, and the traversal also descends
  through arrays. There are two special forms:
  
  - Array items that are simple value types (such as strings) match on a key-value
    pair where the key is the item as a string, and the value is the string &#x60;true&#x60;
  - Relations can be matched on a &#x60;relations.&lt;type&gt;&#x3D;&lt;targetRef&gt;&#x60; form
  
  Let&#39;s look at a simplified example to illustrate the concept:
  
  &#x60;&#x60;&#x60;json
  {
    &quot;a&quot;: {
      &quot;b&quot;: [&quot;c&quot;, { &quot;d&quot;: 1 }],
      &quot;e&quot;: 7
    }
  }
  &#x60;&#x60;&#x60;
  
  This would match any one of the following conditions:
  
  - &#x60;a&#x60;
  - &#x60;a.b&#x60;
  - &#x60;a.b.c&#x60;
  - &#x60;a.b.c&#x3D;true&#x60;
  - &#x60;a.b.d&#x60;
  - &#x60;a.b.d&#x3D;1&#x60;
  - &#x60;a.e&#x60;
  - &#x60;a.e&#x3D;7&#x60;
  
  Some more real world usable examples:
  
  - Return all orphaned entities:
  
    &#x60;/entities/by-query?filter&#x3D;metadata.annotations.backstage.io/orphan&#x3D;true&#x60;
  
  - Return all users and groups:
  
    &#x60;/entities/by-query?filter&#x3D;kind&#x3D;user&amp;filter&#x3D;kind&#x3D;group&#x60;
  
  - Return all service components:
  
    &#x60;/entities/by-query?filter&#x3D;kind&#x3D;component,spec.type&#x3D;service&#x60;
  
  - Return all entities with the &#x60;java&#x60; tag:
  
    &#x60;/entities/by-query?filter&#x3D;metadata.tags.java&#x60;
  
  - Return all users who are members of the &#x60;ops&#x60; group (note that the full
    [reference](references.md) of the group is used):
  
    &#x60;/entities/by-query?filter&#x3D;kind&#x3D;user,relations.memberof&#x3D;group:default/ops&#x60;
  
       */
  async getEntityFacets(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/entity-facets{?facet*,filter*}`;
    const uri = parser.parse(uriTemplate).expand({
      ...request.query
    });
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "GET"
    });
  }
  /**
   * Refresh the entity related to entityRef.
   * @param refreshEntityRequest -
   */
  async refreshEntity(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/refresh`;
    const uri = parser.parse(uriTemplate).expand({});
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "POST",
      body: JSON.stringify(request.body)
    });
  }
  /**
   * Validate that a passed in entity has no errors in schema.
   * @param validateEntityRequest -
   */
  async validateEntity(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/validate-entity`;
    const uri = parser.parse(uriTemplate).expand({});
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "POST",
      body: JSON.stringify(request.body)
    });
  }
  /**
   * Validate a given location.
   * @param analyzeLocationRequest -
   */
  async analyzeLocation(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/analyze-location`;
    const uri = parser.parse(uriTemplate).expand({});
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "POST",
      body: JSON.stringify(request.body)
    });
  }
  /**
   * Create a location for a given target.
   * @param createLocationRequest -
   * @param dryRun -
   */
  async createLocation(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/locations{?dryRun}`;
    const uri = parser.parse(uriTemplate).expand({
      ...request.query
    });
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "POST",
      body: JSON.stringify(request.body)
    });
  }
  /**
   * Delete a location by id.
   * @param id -
   */
  async deleteLocation(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/locations/{id}`;
    const uri = parser.parse(uriTemplate).expand({
      id: request.path.id
    });
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "DELETE"
    });
  }
  /**
   * Get a location by id.
   * @param id -
   */
  async getLocation(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/locations/{id}`;
    const uri = parser.parse(uriTemplate).expand({
      id: request.path.id
    });
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "GET"
    });
  }
  /**
   * Get a location for entity.
   * @param kind -
   * @param namespace -
   * @param name -
   */
  async getLocationByEntity(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/locations/by-entity/{kind}/{namespace}/{name}`;
    const uri = parser.parse(uriTemplate).expand({
      kind: request.path.kind,
      namespace: request.path.namespace,
      name: request.path.name
    });
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "GET"
    });
  }
  /**
   * Get all locations
   */
  async getLocations(request, options) {
    const baseUrl = await this.discoveryApi.getBaseUrl(pluginId);
    const uriTemplate = `/locations`;
    const uri = parser.parse(uriTemplate).expand({});
    return await this.fetchApi.fetch(`${baseUrl}${uri}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.token && { Authorization: `Bearer ${options?.token}` }
      },
      method: "GET"
    });
  }
}

export { DefaultApiClient };
//# sourceMappingURL=Api.client.esm.js.map
